import { createSlice } from '@reduxjs/toolkit';

const calculateTotals = (items, bundles) => {
  const totalAmount =
    items.reduce((sum, item) => sum + item.totalAmount, 0) +
    bundles.reduce(
      (sum, bundle) =>
        sum +
        (bundle.totalAmount || bundle.quantity * (bundle.unitPrice || bundle.price || 0) || 0),
      0
    );

  // Collect SKU IDs that belong to bundles
  const bundleSkuIds = new Set();
  (bundles || []).forEach((b) => {
    (b.bundleProduct || []).forEach((bp) => {
      if (bp?.sku?.sku_id) bundleSkuIds.add(bp.sku.sku_id);
    });
    (b.bundleProducts || []).forEach((bp) => {
      if (bp?.sku_id) bundleSkuIds.add(bp.sku_id);
    });
  });

  const standaloneQty = (items || [])
    .filter((item) => !bundleSkuIds.has(item.sku_id))
    .reduce((sum, item) => sum + (item.quantity || 0), 0);

  const bundleQty = (bundles || []).reduce((sum, bundle) => {
    const itemsInBundle = (bundle.bundleProduct || bundle.bundleProducts || []).reduce(
      (acc, bp) => acc + (bp.bundle_quantity || bp.bundleQuantity || bp.quantity || 1),
      0
    );
    return sum + (bundle.quantity || 0) * (itemsInBundle || 1);
  }, 0);
  const totalQuantity = standaloneQty + bundleQty;

  return { totalAmount, totalQuantity };
};

const findItemById = (items, id, type) =>
  type === 'bundle'
    ? items.find((bundle) => bundle.bundle_id === id)
    : items.find((item) => item.product_id === id);

const initialState = {
  cartId: null,
  eventCartIds: {},
  items: [],
  bundle: [],
  orderSummary: {},
  totalAmount: 0,
  totalQuantity: 0,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartId: (state, action) => {
      const { eventId, cartId } = action.payload || {};
      state.cartId = cartId || null;
      if (eventId) {
        state.eventCartIds[eventId] = cartId || null;
      }
    },

    setCart: (state, action) => {
      const {
        items = [],
        bundle = [],
        orderSummary = {},
        totalAmount = 0,
        totalQuantity = 0,
      } = action.payload || {};

      state.items = items;
      state.bundle = bundle;
      state.orderSummary = orderSummary;
      state.totalAmount = totalAmount;
      state.totalQuantity = totalQuantity;
    },

    clearCart: (state) => {
      state.cartId = null;
      state.eventCartIds = {};
      state.items = [];
      state.bundle = [];
      state.orderSummary = {};
      state.totalAmount = 0;
      state.totalQuantity = 0;
    },

    addToCart: (state, action) => {
      const item = action.payload;

      if (item?.product_id) {
        const existingProduct = findItemById(state.items, item.product_id);

        const itemPrice =
          item?.product_details?.skus[0]?.discount_price ??
          item?.product_details?.skus[0]?.regular_price;

        if (existingProduct) {
          existingProduct.quantity += 1;
          existingProduct.totalAmount = existingProduct.quantity * itemPrice;
        } else {
          state.items.push({
            ...item,
            quantity: 1,
            totalAmount: itemPrice,
          });
        }
      } else if (item?.bundle_id) {
        const existingBundle = findItemById(state.bundle, item.bundle_id, 'bundle');

        if (existingBundle) {
          existingBundle.quantity += 1;
          existingBundle.totalAmount = existingBundle.quantity * existingBundle.unitPrice;
        } else {
          state.bundle.push({
            ...item,
            quantity: 1,
            totalAmount: item.totalAmount,
          });
        }
      }

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;
    },

    addByQuantity: (state, action) => {
      const newItems = action.payload;

      newItems.forEach((newItem) => {
        const existingItem = findItemById(state.items, newItem.product_id);

        if (existingItem) {
          existingItem.quantity += newItem.quantity;
          existingItem.totalAmount = existingItem.quantity * newItem.price;
        } else {
          state.items.push({
            ...newItem,
            totalAmount: newItem.price * newItem.quantity,
          });
        }
      });

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;
    },

    removeFromCart: (state, action) => {
      const payload = action.payload || {};
      const isBundle = payload.type === 'bundle' || !!payload.bundle_id;
      const itemId = payload.itemId || payload.product_id || payload.bundle_id;
      const list = isBundle ? state.bundle : state.items;

      const index = list.findIndex((item) =>
        isBundle ? item.bundle_id === itemId : item.product_id === itemId
      );

      if (index > -1) {
        const existingItem = list[index];
        if (existingItem.quantity > 1) {
          const previousQty = existingItem.quantity;
          existingItem.quantity -= 1;
          const unitPrice = (existingItem.totalAmount || 0) / previousQty;
          existingItem.totalAmount = existingItem.quantity * unitPrice;
        } else {
          list.splice(index, 1);
        }
      }

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;
    },

    removeItemFromCart: (state, action) => {
      const { itemId } = action.payload;

      state.items = state.items.filter((item) => item.product_id !== itemId);

      const totals = calculateTotals(state.items, state.bundle);
      state.totalAmount = totals.totalAmount;
      state.totalQuantity = totals.totalQuantity;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  removeItemFromCart,
  addByQuantity,
  setCart,
  clearCart,
  setCartId,
} = cartSlice.actions;

export default cartSlice.reducer;
